/* eslint-disable import/prefer-default-export */
import type { AxiosResponse } from 'axios';
import { useQuery, useQueries } from 'react-query';
import { getChalktalkPracticeDetails } from '@apis/practice';

type ApiResponse<T> = { response: AxiosResponse & { data: T } };

interface Asset {
  id: number;
  asset_type: string;
  asset_type_code: number;
  text: string;
  image: string | null;
}

interface QuestionDetail {
  id: number;
  question_format: number;
  prompt: {
    text: string;
    image: string | null;
  };
  choices: [];
  shared_assets: Asset[];
  explanation: {
    text: string;
  };
  nodes: number[];
}

type GetQuestionQueryConfig = {
  queryKey: ['useGetQuestionDetails', number];
  queryFn: () => Promise<ApiResponse<QuestionDetail>>;
  useErrorBoundary: boolean;
  suspense: boolean;
  enabled: boolean;
  staleTime: number;
};

const getQuestionQueryConfig = (id: number): GetQuestionQueryConfig => ({
  queryKey: ['useGetQuestionDetails', id],
  queryFn: async () => getChalktalkPracticeDetails(id),
  useErrorBoundary: true,
  suspense: false,
  enabled: Boolean(id),
  staleTime: 1000 * 60 * 60 * 24,
});

interface QuestionDetailResponse {
  id: number;
  exam_question: {
    id: number;
    order: number;
    question: QuestionDetail;
    section?: number | null;
  };
  question_response: {
    choices: Asset[];
    response_time: number;
  };
  score: {
    auto: number;
    auto_max: number;
    manual: number;
    manual_max: number;
  };
}

export const useGetQuestionDetails = (id: number) => {
  const queryResponse = useQuery(getQuestionQueryConfig(id));
  const rawData = queryResponse?.data?.response?.data;
  if (!rawData) {
    return {
      data: undefined,
      isLoading: false,
    };
  }
  const questionDetail = {
    id: rawData.id,
    exam_question: {
      id: rawData.id,
      order: 1,
      question: rawData,
      section: null,
    },
    question_response: {
      choices: [],
      response_time: 0,
    },
    score: {
      auto: 0,
      auto_max: 0,
      manual: 0,
      manual_max: 0,
    },
  };
  return {
    data: questionDetail as QuestionDetailResponse,
    isLoading: queryResponse?.isLoading || false,
  };
};

export const useGetMultipleQuestionDetails = (
  questionIds: number[],
  examQuestionIds: Record<number, number>,
) => {
  const queryResults = useQueries(questionIds.map((qId) => getQuestionQueryConfig(qId)));

  return {
    data: queryResults.map((res, idx) => {
      const rawData = res.data?.response?.data;
      if (!rawData) {
        return undefined;
      }

      // Find the eqId using the questionId we fetched
      const eqId = Object.keys(examQuestionIds).find(
        (key) => examQuestionIds[key] === rawData.id,
      );

      return {
        id: Number(eqId),
        exam_question: {
          id: Number(eqId),
          order: idx + 1,
          question: rawData,
          section: null, // Fixed typo from 'secton'
        },
        question_response: {
          choices: [],
          response_time: 0,
        },
        score: {
          auto: 0,
          auto_max: 0,
          manual: 0,
          manual_max: 0,
        },
      };
    }),
    isLoading: queryResults.some((res) => res.isLoading),
    isAllSuccess: queryResults.every((res) => res.isSuccess),
  };
};
